import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Card, CardHeader, CardContent, CardFooter } from '../ui/card';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ControllerRenderProps } from 'react-hook-form';
import { AppPage } from '@/types/navigation';

const formSchema = z.object({
  email: z.string().email('Invalid email address'),
});

interface ResetPasswordProps {
  onNavigate: (page: AppPage) => void;
}

export default function ResetPassword({ onNavigate }: ResetPasswordProps) {
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setMessage('');
      setError('');
      setLoading(true);
      await resetPassword(values.email);
      setMessage('Check your inbox for further instructions');
    } catch (err) {
      setError('Failed to reset password');
      console.error(err);
    }
    setLoading(false);
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <Card className="w-full">
          <CardHeader className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Reset Password</h1>
            <p className="text-sm text-muted-foreground">Enter your email to reset your password</p>
          </CardHeader>

          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {message && (
              <Alert className="mb-4">
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }: { field: ControllerRenderProps<{email: string}, "email"> }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="m@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={loading}>
                  Reset Password
                </Button>
              </form>
            </Form>
          </CardContent>

          <CardFooter className="flex flex-col gap-2">
            <Button
              variant="link"
              className="px-0 font-normal"
              onClick={() => onNavigate('login')}
            >
              Back to Login
            </Button>
            <p className="text-sm text-muted-foreground w-full text-center">
              Need an account?{" "}
              <Button
                variant="link"
                className="px-0 font-normal"
                onClick={() => onNavigate('signup')}
              >
                Sign Up
              </Button>
            </p>
            <Button
              variant="link"
              className="px-0 font-normal"
              onClick={() => onNavigate('main')}
            >
              Back to Main Page
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
} 